import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const placeholderImages = [
  {
    id: '1',
    title: 'Entrenamiento de equipo',
    image_url: 'https://images.unsplash.com/photo-1562552052-dbdd31c06339?crop=entropy&cs=srgb&fm=jpg&q=85',
  },
  {
    id: '2',
    title: 'Práctica de tiros',
    image_url: 'https://images.unsplash.com/photo-1665256452878-b2983482c50d?crop=entropy&cs=srgb&fm=jpg&q=85',
  },
  {
    id: '3',
    title: 'Torneo juvenil',
    image_url: 'https://images.pexels.com/photos/974502/pexels-photo-974502.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    id: '4',
    title: 'Celebración de victoria',
    image_url: 'https://images.pexels.com/photos/6777170/pexels-photo-6777170.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    id: '5',
    title: 'Trabajo en equipo',
    image_url: 'https://images.unsplash.com/photo-1519861531473-9200262188bf?q=80&w=2071',
  },
  {
    id: '6',
    title: 'Sesión de entrenamiento',
    image_url: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?q=80&w=2070',
  },
];

export function GallerySection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [images, setImages] = useState(placeholderImages);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const { data } = await axios.get(`${API}/api/gallery`);
      if (data.length > 0) {
        setImages(data);
      }
    } catch (error) {
      console.log('Using placeholder images');
    }
  };

  return (
    <section 
      id="galeria" 
      className="py-24 px-4 sm:px-6 lg:px-8 bg-black"
      ref={ref}
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="text-orange-500 font-semibold tracking-widest text-sm">NUESTRAS EXPERIENCIAS</span>
          <h2 
            className="text-4xl sm:text-5xl font-extrabold text-white mt-2 mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="gallery-title"
          >
            GALERÍA
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Momentos que capturan la pasión, el esfuerzo y la alegría de nuestra comunidad deportiva.
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((image, index) => (
            <motion.div
              key={image.id}
              className={`gallery-item rounded-lg cursor-pointer ${
                index === 0 ? 'md:col-span-2 md:row-span-2' : ''
              }`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onClick={() => setSelectedImage(image)}
              data-testid={`gallery-image-${index}`}
            >
              <img
                src={image.image_url}
                alt={image.title}
                className={`w-full object-cover rounded-lg ${
                  index === 0 ? 'h-[300px] md:h-[500px]' : 'h-[200px] md:h-[240px]'
                }`}
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 hover:opacity-100 transition-opacity z-10">
                <p className="text-white font-semibold">{image.title}</p>
                {image.description && (
                  <p className="text-zinc-300 text-sm">{image.description}</p>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => setSelectedImage(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-orange-500 transition-colors"
              onClick={() => setSelectedImage(null)}
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>
            <img
              src={selectedImage.image_url}
              alt={selectedImage.title}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <div className="absolute bottom-8 left-0 right-0 text-center">
              <p className="text-white text-xl font-semibold">{selectedImage.title}</p>
              {selectedImage.description && (
                <p className="text-zinc-400">{selectedImage.description}</p>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
